/**
 * Form Options Constants
 * Centralized constants for form dropdown/select options
 */

/** Lucide icon stroke width (matches Radiology V2 reference) */
export const ICON_STROKE_WIDTH = 2;

/** Z-index for Change Password modal so it appears above table and dropdowns */
export const Z_INDEX_CHANGE_PASSWORD_MODAL = 9999;

/** DataTable column alignment → CSS class (cell) */
export const DATA_TABLE_ALIGN_MAP = {
  center: 'data-table-cell-center',
  right: 'data-table-cell-right',
} as const;
/** DataTable column alignment → CSS class (header) */
export const DATA_TABLE_HEADER_ALIGN_MAP = {
  center: 'data-table-header-cell-center',
  right: 'data-table-header-cell-right',
} as const;

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Gender options for user forms
 */
export const GENDER_OPTIONS: SelectOption[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

/**
 * Role options for user forms (Add/Edit User dialog) - Radiologist, Technician, and Referring Physician
 */
export const ROLE_OPTIONS: SelectOption[] = [
  { value: 'Radiologist', label: 'Radiologist' },
  { value: 'Technician', label: 'Technician' },
  { value: 'ReferringPhysician', label: 'Referring Physician' },
];

/**
 * Status options for user forms and filters
 */
export const STATUS_OPTIONS: SelectOption[] = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

/**
 * Sub-role options for Radiologist (API expects "Junior" / "Senior" when hierarchy is enabled)
 */
export const SUB_ROLE_OPTIONS: SelectOption[] = [
  { value: 'Junior', label: 'Junior Radiologist' },
  { value: 'Senior', label: 'Senior Radiologist' },
];

/**
 * Specialty options for Radiologist (Edit/Create User).
 * Selected value is sent in the specialty key in the payload.
 */
export const SPECIALTY_OPTIONS: SelectOption[] = [
  { value: 'Neuroradiology', label: 'Neuroradiology' },
  { value: 'MSK', label: 'MSK' },
  { value: 'Chest', label: 'Chest' },
  { value: 'Abdominal', label: 'Abdominal' },
  { value: 'Breast', label: 'Breast' },
  { value: 'Pediatric', label: 'Pediatric' },
  { value: 'Emergency', label: 'Emergency' },
  { value: 'Cardiac', label: 'Cardiac' },
];

/**
 * Availability day options for Create/Edit User (M–S buttons)
 */
export const AVAILABILITY_DAY_OPTIONS: { key: string; label: string }[] = [
  { key: 'Mon', label: 'M' },
  { key: 'Tue', label: 'T' },
  { key: 'Wed', label: 'W' },
  { key: 'Thu', label: 'T' },
  { key: 'Fri', label: 'F' },
  { key: 'Sat', label: 'S' },
  { key: 'Sun', label: 'S' },
];

/**
 * Availability hours options for Radiologist (dropdown)
 */
export const AVAILABILITY_HOURS_OPTIONS: SelectOption[] = [
  { value: '8:00 AM - 5:00 PM', label: '8:00 AM - 5:00 PM' },
  { value: '9:00 AM - 6:00 PM', label: '9:00 AM - 6:00 PM' },
  { value: '10:00 AM - 7:00 PM', label: '10:00 AM - 7:00 PM' },
  { value: '8:00 AM - 2:00 PM', label: '8:00 AM - 2:00 PM' },
  { value: '02:00 PM - 04:00 PM', label: '02:00 PM - 04:00 PM' },
  { value: '2:00 PM - 10:00 PM', label: '2:00 PM - 10:00 PM' },
  { value: '24 Hours', label: '24 Hours' },
];

/**
 * Modality options for radiologist reporting_modality_access (API: CT, MR, US, XR only)
 */
export const MODALITY_OPTIONS: SelectOption[] = [
  { value: 'CT', label: 'CT' },
  { value: 'MR', label: 'MR' },
  { value: 'US', label: 'US' },
  { value: 'XR', label: 'XR' },
];

/** Modality options for Report Templates (filters + create/edit modal) */
export const TEMPLATE_MODALITY_OPTIONS: SelectOption[] = [
  { value: 'MR', label: 'MRI' },
  { value: 'CT', label: 'CT' },
  { value: 'XR', label: 'X-Ray' },
  { value: 'US', label: 'USG' },
];

/** Same pattern as User Management filter dropdowns (FormSelectVirtualized) */
export const TEMPLATE_STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const TEMPLATE_MODALITY_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All modalities' },
  ...TEMPLATE_MODALITY_OPTIONS.map(o => ({ value: o.value, label: o.label })),
];

/** Tenant `/ris/api/templates?type=SYSTEM|CUSTOM` — matches Figma “All types” row */
export const TEMPLATE_TYPE_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All types' },
  { value: 'system', label: 'System' },
  { value: 'custom', label: 'Custom' },
];

/** Templates table rows per page (matches Figma density) */
export const TEMPLATES_ITEMS_PER_PAGE = 10;

export const TEMPLATES_FACILITY_SEARCH_DEBOUNCE_MS = 300;

/** Initial fetch for template list (client-side filter/pagination) */
export const TEMPLATES_API_FETCH_LIMIT = 100;

export const TEMPLATES_FACILITIES_FILTER_PAGE_LIMIT = 25;

export const TEMPLATES_FACILITIES_MODAL_PAGE_LIMIT = 100;

/** Max words for template rich-text (combined findings + impression, or single document-import body). */
export const TEMPLATE_MAX_CONTENT_WORDS = 10000;

export const TEMPLATE_CONTENT_VALIDATION_DELAY_MS = 500;

/** File extensions accepted by template import (file picker + validation) */
export const TEMPLATE_IMPORT_SUPPORTED_EXTENSIONS: readonly string[] = [
  'json',
  'pdf',
  'txt',
  'doc',
  'docx',
];

/**
 * When more than this many document files are uploaded (or a folder contains more),
 * open the bulk “Review uploaded files” modal (per-file modality) instead of Create Template.
 */
export const TEMPLATE_BULK_UPLOAD_AUTO_CREATE_THRESHOLD = 5;

/** Folder import safety caps (File System Access / directory walk). */
export const TEMPLATE_FOLDER_IMPORT_MAX_DEPTH = 8;
export const TEMPLATE_FOLDER_IMPORT_MAX_FILES = 200;
/** 50 MiB total raw file size across the folder selection. */
export const TEMPLATE_FOLDER_IMPORT_MAX_TOTAL_BYTES = 50 * 1024 * 1024;

/** Max pages when prefetching templates for upload duplicate checks (page size = TEMPLATES_API_FETCH_LIMIT). */
export const TEMPLATE_UPLOAD_DUPLICATE_CHECK_MAX_PAGES = 50;

/**
 * RIS Findings / Impressions / document Template content — match Figma tool strip (15 controls in 3 groups).
 * Insert Table is not included: stock Quill 1.x has no supported table blot/toolbar hook without a Quill 2–aligned module.
 */
export const TEMPLATE_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike', { list: 'bullet' }, { list: 'ordered' }],
    [{ align: '' }, { align: 'center' }, { align: 'right' }],
    [{ indent: '-1' }, { indent: '+1' }, 'link', 'image', { script: 'super' }, { script: 'sub' }],
  ],
} as const;

/** Allowed formats when using {@link TEMPLATE_QUILL_MODULES} (keeps pasted content aligned with visible tools). */
export const TEMPLATE_QUILL_FORMATS: string[] = [
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'indent',
  'align',
  'link',
  'image',
  'script',
];

/**
 * Create/Edit Template modal dialog shell.
 * DialogContent defaults include `w-full` + `max-w-md`; explicit width/max-width here so tailwind-merge
 * replaces those utilities — otherwise the panel stretches to 100% viewport width.
 */
export const CREATE_TEMPLATE_MODAL_DIALOG_CLASS =
  'create-template-modal-dialog !flex !flex-col !min-h-0 !gap-0 !bg-white !p-0 !rounded-[7.2px] [&>div]:!mt-0 !w-[min(820px,calc(100vw-32px))] !max-w-[min(820px,calc(100vw-32px))] box-border';

/** Upload Template modal — Figma: narrower dialog (~1/3 viewport on desktop) */
export const UPLOAD_TEMPLATE_MODAL_DIALOG_CLASS =
  'upload-template-modal-dialog !flex !flex-col !min-h-0 !gap-0 !bg-white !p-0 !rounded-[7.2px] [&>div]:!mt-0 !w-[min(440px,calc(100vw-32px))] !max-w-[min(440px,calc(100vw-32px))] box-border';

/**
 * Bulk upload review (>5 files / large folder): file name list + per-file modality, then create all.
 */
export const BULK_UPLOAD_REVIEW_MODAL_DIALOG_CLASS =
  'bulk-upload-review-modal-dialog !flex !flex-col !min-h-0 !gap-0 !bg-white !p-0 !rounded-[7.2px] [&>div]:!mt-0 !w-[min(720px,calc(100vw-32px))] !max-w-[min(720px,calc(100vw-32px))] !max-h-[min(720px,calc(100vh-48px))] box-border overflow-hidden';

/** Radiologist “Import from library” — catalog list modal (Figma card list) */
export const IMPORT_LIBRARY_MODAL_DIALOG_CLASS =
  'import-library-modal-dialog !flex !flex-col !min-h-0 !gap-0 !bg-white !p-0 !rounded-[8px] [&>div]:!mt-0 !w-[min(620px,calc(100vw-32px))] !max-w-[min(620px,calc(100vw-32px))] !max-h-[min(680px,calc(100vh-48px))] box-border overflow-hidden shadow-sm';

/** Edit/View Template modal shell */
export const EDIT_VIEW_TEMPLATE_MODAL_DIALOG_CLASS =
  'edit-view-template-modal-dialog !flex !flex-col !min-h-0 !gap-0 !bg-white !p-0 !rounded-[7.2px] [&>div]:!mt-0 !w-[min(820px,calc(100vw-32px))] !max-w-[min(820px,calc(100vw-32px))] box-border';

/** Tenant “View Update” — current vs master catalogue (two-column compare) */
export const VIEW_UPDATE_TEMPLATE_MODAL_DIALOG_CLASS =
  'view-update-template-modal-dialog !flex !flex-col !min-h-0 !gap-0 !bg-white !p-0 !rounded-[8px] [&>div]:!mt-0 !w-[min(960px,calc(100vw-32px))] !max-w-[min(960px,calc(100vw-32px))] !max-h-[min(720px,calc(100vh-40px))] box-border overflow-hidden';

/** How SignaturePreviewDialog embeds the file (iframe vs img vs fallback link). */
export const SIGNATURE_PREVIEW_KIND = {
  PDF: 'pdf',
  IMAGE: 'image',
  OTHER: 'other',
} as const;

export type SignaturePreviewKind =
  (typeof SIGNATURE_PREVIEW_KIND)[keyof typeof SIGNATURE_PREVIEW_KIND];

export const MIME_TYPE_APPLICATION_PDF = 'application/pdf';

/** Coarse image MIME hint when inferring from filename only. */
export const MIME_TYPE_IMAGE_WILDCARD = 'image/*';

/** Signature-friendly image extensions (query string should be stripped before testing). */
export const SIGNATURE_IMAGE_FILE_EXTENSION_PATTERN = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

/** HTML `accept` for signature uploads — JPEG and PNG only. */
export const SIGNATURE_UPLOAD_ACCEPT = 'image/jpeg,image/png,.jpg,.jpeg,.png';

/** Shown when the user picks a non–JPG/PNG file for signature. */
export const SIGNATURE_UPLOAD_TYPE_ERROR = 'Signature must be a JPG or PNG image.';

/** Allowed signature upload: `image/jpeg` or `image/png`; if MIME is empty, `.jpg`/`.jpeg`/`.png` only. */
export function isAllowedSignatureUploadFile(file: File): boolean {
  const mime = (file.type || '').toLowerCase();
  if (mime === 'image/jpeg' || mime === 'image/png') {
    return true;
  }
  if (mime) {
    return false;
  }
  const q = file.name.indexOf('?');
  const h = file.name.indexOf('#');
  let cut = file.name.length;
  if (q !== -1) {
    cut = Math.min(cut, q);
  }
  if (h !== -1) {
    cut = Math.min(cut, h);
  }
  const base = file.name.slice(0, cut);
  return /\.(jpe?g|png)$/i.test(base);
}

/**
 * Infer a coarse MIME hint from the file name (query string stripped) for remote preview without fetch.
 */
export function inferMimeTypeHintFromSignatureFileName(
  fileName: string | null | undefined
): string {
  const nameWithoutQuery = (fileName || '').split('?')[0];
  const lowerBaseName = nameWithoutQuery.toLowerCase();
  if (lowerBaseName.endsWith('.pdf')) {
    return MIME_TYPE_APPLICATION_PDF;
  }
  if (SIGNATURE_IMAGE_FILE_EXTENSION_PATTERN.test(lowerBaseName)) {
    return MIME_TYPE_IMAGE_WILDCARD;
  }
  return '';
}

/**
 * Decide how to preview a signature file from MIME type and/or file name.
 */
export function getSignatureFilePreviewKind(
  mimeType: string,
  fileName: string | null | undefined
): SignaturePreviewKind {
  const lowerFileName = (fileName || '').toLowerCase();
  const nameWithoutQuery = lowerFileName.split('?')[0];
  if (mimeType === MIME_TYPE_APPLICATION_PDF || nameWithoutQuery.endsWith('.pdf')) {
    return SIGNATURE_PREVIEW_KIND.PDF;
  }
  if (mimeType.startsWith('image/')) {
    return SIGNATURE_PREVIEW_KIND.IMAGE;
  }
  if (SIGNATURE_IMAGE_FILE_EXTENSION_PATTERN.test(nameWithoutQuery)) {
    return SIGNATURE_PREVIEW_KIND.IMAGE;
  }
  return SIGNATURE_PREVIEW_KIND.OTHER;
}
